import React,{useEffect,useMemo,useState} from 'react';
import { ArrowLeft, Check, ChevronRight, Database, Search, X } from 'lucide-react';
import MetricusInput from './ui/MetricusInput';
import MetricusSelect from './ui/MetricusSelect';
import MetricusDataGrid from './ui/MetricusDataGrid';
import OrganizationContextStep,{EMPTY_ORGANIZATION_CONTEXT} from './OrganizationContextStep';

const fmt=n=>new Intl.NumberFormat().format(Number(n||0));
const DEFAULT_MODEL={
  name:'ServiceNow Process Model',
  processType:'Incident Management',
  category:'ITSM',
  entityLabel:'Record',
  businessObjective:'',
  description:'',
};

function Stepper({stage}){
  const steps=['Scope','Model details','Organization context','Activity & attributes','Preview','Run'];
  return <div className="sn-stepper">
    {steps.map((label,i)=>{
      const n=i+1,done=n<stage,active=n===stage;
      return <React.Fragment key={label}>
        <div className={`sn-step ${done?'done':''} ${active?'active':''}`}>
          <span>{done?<Check size={14}/>:n}</span><b>{label}</b>
        </div>
        {i<steps.length-1&&<div className={`sn-step-line ${done?'done':''}`}/>}
      </React.Fragment>;
    })}
  </div>;
}

function WorkingDialog({title,detail}){
  return <div className="sn-working-overlay" role="status" aria-live="polite" aria-busy="true">
    <div className="sn-working-dialog">
      <div className="sn-working-spinner" aria-hidden="true"/>
      <div>
        <b>{title}</b>
        <span>{detail}</span>
      </div>
    </div>
  </div>;
}

export default function ServiceNowRestBuilder({
  repository,connectionId,connection,onBack,onRunProcessAdvisor,accessStatus=null,initial=null,initialStage=1
}){
  const [stage,setStage]=useState(Number(initialStage||1));
  const [metadata,setMetadata]=useState(null);
  const [busy,setBusy]=useState('');
  const [error,setError]=useState('');
  const [table,setTable]=useState(initial?.source?.table||initial?.source?.scope?.table||'incident');
  const [query,setQuery]=useState(initial?.source?.query||'');
  const [fromDate,setFromDate]=useState(initial?.source?.fromDate||'');
  const [toDate,setToDate]=useState(initial?.source?.toDate||'');
  const [scopeCount,setScopeCount]=useState(Number(initial?.summary?.configuredCases||initial?.summary?.cases||0)||null);
  const [scopeCountCurrent,setScopeCountCurrent]=useState(Boolean(initial?.summary?.configuredCases||initial?.summary?.cases));
  const [model,setModel]=useState({
    ...DEFAULT_MODEL,
    name:initial?.name||DEFAULT_MODEL.name,
    processType:initial?.processType||DEFAULT_MODEL.processType,
    category:initial?.category||DEFAULT_MODEL.category,
    entityLabel:initial?.source?.entityLabel||'Record',
    businessObjective:initial?.source?.businessObjective||'',
    description:initial?.description||'',
  });
  const [organizationContext,setOrganizationContext]=useState(()=>({...EMPTY_ORGANIZATION_CONTEXT,...(initial?.organizationContext||{})}));
  const [activityFieldId,setActivityFieldId]=useState(initial?.source?.activityField?.id||'state');
  const [selectedFieldIds,setSelectedFieldIds]=useState(initial?.source?.selectedFieldIds||[]);
  const [attributeNotes,setAttributeNotes]=useState(initial?.source?.attributeNotes||{});
  const [search,setSearch]=useState('');
  const [preview,setPreview]=useState(null);
  const [retainProcessData,setRetainProcessData]=useState(initial?.persistence?.retainProcessData ?? initial?.persistence?.storageConsent ?? true);
  const [runAccessChoice,setRunAccessChoice]=useState('free');
  const [runResult,setRunResult]=useState(null);
  const [extractProgress,setExtractProgress]=useState({percent:0,status:'Preparing',step:'Starting',detail:'Preparing the ServiceNow extraction.'});

  const fields=metadata?.fields||[];
  const conn=connection||metadata?.connection||{};
  const fieldName=f=>f?.label||f?.name||f?.id||'Field';
  const CORE_FIELD_IDS=new Set(['sys_id','number','short_description','sys_created_on','opened_at','closed_at','resolved_at']);
  const activityField=fields.find(f=>f.id===activityFieldId)||{id:activityFieldId,label:activityFieldId,name:activityFieldId};
  const selectedAttributeFields=selectedFieldIds.map(id=>fields.find(f=>f.id===id)).filter(Boolean);
  const freeLimit=Number(accessStatus?.freeAssessment?.processingUnitLimit||accessStatus?.freeAssessment?.caseLimit||250);
  const availableAssessments=Number(accessStatus?.availableAssessments??accessStatus?.assessmentCredits?.balance??0);
  const sourceSubscription=(accessStatus?.subscriptions||[]).find(s=>s?.active&&String(s?.sourceConnectionId||'')===String(connectionId||''));
  const initialAssessmentActive=['assessment','assessment_credit'].includes(String(initial?.currentAccess?.source||initial?.accessSource||''));
  const paidBySubscription=Boolean(accessStatus?.subscription?.active);
  const paidByAssessment=false;
  const hasPaidAccess=paidBySubscription;
  const visibleProcessingLimit=hasPaidAccess?Number(scopeCount||0):Math.min(Number(scopeCount||0),freeLimit);
  const partialRun=Number(scopeCount||0)>visibleProcessingLimit;
  const workingDialog=busy==='metadata'
    ? {title:'Loading ServiceNow metadata…',detail:'Reading the Task table hierarchy and fields from ServiceNow.'}
    : (busy==='count'||busy==='validate')
      ? {title:'Counting records…',detail:'Checking how many ServiceNow records match the current scope.'}
      : null;

  useEffect(()=>{
    if(busy!=='run')return;
    const start=Date.now();
    setExtractProgress({percent:2,status:'Preparing',step:'Starting',detail:'Preparing the selected ServiceNow process model.'});
    const timer=setInterval(()=>{
      const elapsed=(Date.now()-start)/1000;
      setExtractProgress(prev=>{
        if(prev.percent>=94)return prev;
        const next=Math.min(94,Math.max(prev.percent+1,Math.round(4+elapsed*1.6)));
        let status='Preparing',step='Starting',detail='Preparing the selected ServiceNow process model.';
        if(next>=12){status='Loading';step='Retrieving records';detail='Loading the authorized ServiceNow records for this run.';}
        if(next>=35){status='Extracting';step='Reading activity history';detail='Retrieving ServiceNow audit history and reconstructing the activity sequence.';}
        if(next>=68){status='Processing';step='Building canonical model';detail='Converting ServiceNow records and history into the Process Advisor canonical model.';}
        if(next>=86){status='Validating';step='Final checks';detail='Validating records, events and selected characteristics before opening Process Advisor.';}
        return {...prev,percent:next,status,step,detail};
      });
    },850);
    return()=>clearInterval(timer);
  },[busy,scopeCount]);

  const loadMetadata=async nextTable=>{
    setBusy('metadata');setError('');
    try{
      const data=await repository.getServiceNowMetadata(connectionId,nextTable||table);
      setMetadata(data);
      if(!initial){
        if(!activityFieldId||activityFieldId==='state')setActivityFieldId(data.defaults?.activityFieldId||'state');
        if(!selectedFieldIds.length)setSelectedFieldIds(data.defaults?.selectedFieldIds||[]);
      }
    }catch(e){setError(e.message||'Unable to load ServiceNow metadata');}
    finally{setBusy('');}
  };

  useEffect(()=>{loadMetadata(table);},[connectionId,table]);
  useEffect(()=>{
    setRunAccessChoice(accessStatus?.subscription?.active?'subscription':'free');
  },[Boolean(sourceSubscription),Boolean(accessStatus?.subscription?.active),initialAssessmentActive,connectionId]);

  const toggleField=id=>setSelectedFieldIds(ids=>ids.includes(id)?ids.filter(x=>x!==id):[...ids,id]);

  const countScope=async({advance=false}={})=>{
    setBusy(advance?'validate':'count');setError('');
    try{
      const result=await repository.countServiceNowScope(connectionId,{table,query,fromDate,toDate});
      setScopeCount(result.count);
      setScopeCountCurrent(true);
      if(result.count>50000)throw new Error(`This scope contains ${fmt(result.count)} records. Reduce it to 50,000 or fewer.`);
      if(advance)setStage(2);
      return result;
    }catch(e){
      setError(e.message||'Unable to count ServiceNow records');
      return null;
    }finally{setBusy('');}
  };

  const validateScope=async()=>{
    if(scopeCountCurrent&&scopeCount!=null){
      if(scopeCount>50000){
        setError(`This scope contains ${fmt(scopeCount)} records. Reduce it to 50,000 or fewer.`);
        return;
      }
      setStage(2);
      return;
    }
    await countScope({advance:true});
  };

  const loadPreview=async()=>{
    setBusy('preview');setError('');
    try{
      const result=await repository.previewServiceNowScope(connectionId,{
        table,query,fromDate,toDate,activityFieldId,selectedFieldIds,limit:12
      });
      setPreview(result);setStage(5);
    }catch(e){setError(e.message||'Unable to preview ServiceNow data');}
    finally{setBusy('');}
  };

  const run=async()=>{
    setBusy('run');setError('');
    try{
      const definition=await repository.saveModel({
        ...(initial||{}),
        id:initial?.id||undefined,
        name:model.name,
        processType:model.processType,
        category:model.category,
        description:model.description,
        organizationContext,
        sourceType:'servicenow-rest',
        sourceLabel:conn?.name||'ServiceNow',
        source:{
          ...(initial?.source||{}),
          type:'servicenow-rest',
          connectionId,
          connectionName:conn?.name||'',
          baseUrl:conn?.baseUrl||'',
          table,query,fromDate,toDate,
          activityField:{id:activityFieldId,name:fields.find(f=>f.id===activityFieldId)?.label||activityFieldId},
          selectedFieldIds,attributeNotes,
          entityLabel:model.entityLabel,
          businessObjective:model.businessObjective,
          retainProcessData:Boolean(retainProcessData),
          storageConsent:Boolean(retainProcessData),
        },
        persistence:{
          ...(initial?.persistence||{}),
          modelDefinitionSaved:true,
          retainProcessData:Boolean(retainProcessData),
          storageConsent:Boolean(retainProcessData),
        },
        summary:{...(initial?.summary||{}),configuredCases:Number(scopeCount||0)},
        status:initial?.status||'draft',
      });

      let authorization=await repository.authorizeAssessment({
        modelId:definition.id,
        modelName:model.name,
        caseCount:Number(scopeCount||0),
        sourceConnectionId:connectionId,
        storageRequested:Boolean(retainProcessData),
      });

      if(runAccessChoice==='assessment' && authorization?.source==='free'){
        await repository.activateAssessment(definition.id,{caseCount:Number(scopeCount||0),storageRequested:Boolean(retainProcessData)});
        authorization=await repository.authorizeAssessment({
          modelId:definition.id,modelName:model.name,caseCount:Number(scopeCount||0),sourceConnectionId:connectionId,processedUnitCount:Number(scopeCount||0),eventCount:0,storageRequested:Boolean(retainProcessData),
        });
      }

      const expectedPaidAssessment=false;
      if(expectedPaidAssessment && !['assessment','assessment_credit','subscription','workspace_subscription'].includes(String(authorization?.source||''))){
        throw new Error('This model has an activated assessment, but the processing authorization did not recognise it. No free-limit reprocess was started. Refresh Models and try again.');
      }

      const authorizedLimit=Number(authorization?.processingLimit||scopeCount||0);
      const authorizedPartial=Boolean(authorization?.partial || Number(scopeCount||0)>authorizedLimit);
      setExtractProgress({
        percent:2,
        status:'Authorized',
        step:'Starting extraction',
        detail:authorizedPartial
          ? `${fmt(authorizedLimit)} of ${fmt(scopeCount)} records will be processed in this run.`
          : `The complete ${fmt(scopeCount)}-record scope will be processed.`
      });

      const result=await repository.extractServiceNowModel(connectionId,{
        accessAuthorizationId:authorization?.authorizationId||null,
        retainProcessData:Boolean(retainProcessData),
        requestedCaseCount:Number(scopeCount||0),
        processingLimit:authorizedLimit,
        table,query,fromDate,toDate,activityFieldId,selectedFieldIds,attributeNotes,
        model:{...model,organizationContext,id:definition.id,activityLabel:fields.find(f=>f.id===activityFieldId)?.label||activityFieldId},
      });

      // Belt-and-suspenders free-tier guard at the connector boundary. If the
      // running API is stale and returns more than the authorized subset, discard
      // everything beyond the authorization before Evidence/Advisor sees it.
      if(authorization?.source==='free' && result?.dataset?.cases?.length>authorizedLimit){
        const cases=result.dataset.cases.slice(0,authorizedLimit);
        const ids=new Set(cases.map(c=>String(c.id)));
        const events=(result.dataset.events||[]).filter(e=>ids.has(String(e.caseId)));
        result.dataset={...result.dataset,cases,events,metadata:{...(result.dataset.metadata||{}),ingestion:{...(result.dataset.metadata?.ingestion||{}),processedCaseCount:cases.length,processingLimit:authorizedLimit,partialProcessing:true,accessSource:'free'}}};
        result.validation={...(result.validation||{}),summary:{...(result.validation?.summary||{}),cases:cases.length,events:events.length,processedCases:cases.length,processingLimit:authorizedLimit,partialProcessing:true}};
      }

      const summary=result.validation?.summary||{};
      const payload={
        ...definition,
        sourceType:'servicenow-rest',
        sourceLabel:conn?.name||'ServiceNow',
        source:{
          ...(definition.source||{}),
          table,query,fromDate,toDate,
          activityField:{id:activityFieldId,name:fields.find(f=>f.id===activityFieldId)?.label||activityFieldId},
          selectedFieldIds,attributeNotes,
          accessSource:authorization?.source||null,
        },
        summary:{
          ...summary,
          configuredCases:Number(scopeCount||0),
          processedCases:Number(summary.cases||0),
          accessSource:authorization?.source||null,
        },
        latestRuntime:{canonicalDataset:result.dataset,validation:result.validation},
        runtime:{canonicalDataset:result.dataset,validation:result.validation},
        entitlement:authorization,
        accessAuthorizationId:authorization?.authorizationId||null,
        accessSource:authorization?.source||null,
        persistence:{
          ...(definition.persistence||{}),
          modelDefinitionSaved:true,
          retainProcessData:Boolean(retainProcessData),
          storageConsent:Boolean(retainProcessData),
        },
      };
      setRunResult(result);
      setExtractProgress({percent:100,status:'Complete',step:'Model ready',detail:'ServiceNow extraction completed successfully. Opening Process Advisor.'});
      await new Promise(resolve=>setTimeout(resolve,450));
      await onRunProcessAdvisor(payload);
    }catch(e){
      const message=e.message||'Unable to run ServiceNow model';
      setExtractProgress(prev=>({...prev,status:'Failed',step:e?.status===402?'Access required':'Extraction stopped',detail:message}));
      setError(message);
      await new Promise(resolve=>setTimeout(resolve,250));
    }finally{setBusy('');}
  };

  const previewColumns=useMemo(()=>{
    const byId=new Map(fields.map(f=>[f.id,f]));
    const attributeColumns=selectedFieldIds.map(fieldId=>{
      const field=byId.get(fieldId);
      return {
        headerName:fieldName(field),
        field:`attributes.${fieldId}`,
        minWidth:150,
        flex:.8,
        valueGetter:p=>p.data?.attributes?.[fieldId]||'—',
      };
    });
    return [
      {headerName:'Number',field:'key',minWidth:130,pinned:'left',cellClass:'metricus-grid-key'},
      {headerName:'Summary',field:'summary',minWidth:260,flex:1.4,pinned:'left'},
      {headerName:`Current ${fieldName(activityField)}`,field:'activity',minWidth:170,flex:.8},
      ...attributeColumns,
      {headerName:'Created',field:'created',minWidth:165,
        valueFormatter:p=>{
          if(!p.value)return '—';
          const raw=String(p.value);
          const iso=/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(raw)?raw.replace(' ','T')+'Z':raw;
          const d=new Date(iso);
          return Number.isNaN(d.getTime())?raw:new Intl.DateTimeFormat('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}).format(d);
        }
      },
    ];
  },[fields,selectedFieldIds,activityFieldId]);

  return <div className="sn-builder">
    <div className="page-head compact-model-builder-head">
      <div>
        <div className="eyebrow">SERVICENOW REST MODEL</div>
        <h1>{initial?'ServiceNow model':'New ServiceNow model'}</h1>
        <p>Configure the ServiceNow table, process activity and contextual attributes.</p>
      </div>
      <button className="pa-secondary" onClick={onBack}><ArrowLeft size={14}/> Change connection</button>
    </div>

    <Stepper stage={stage}/>

    {error&&<div className="pa-alert error">{error}</div>}
    {workingDialog&&<WorkingDialog title={workingDialog.title} detail={workingDialog.detail}/>}

    {stage===1&&<section className="panel sn-builder-panel">
      <div className="panel-head"><div><h2>Scope</h2><p>Select any Task-related ServiceNow table and the records that belong in this process model.</p></div><div className="source-chip"><Database size={15}/>{conn?.name||'ServiceNow'}</div></div>
      <div className="panel-body sn-scope-body">
        <div className="sn-scope-grid">
          <label><span>Table</span><select value={table} onChange={e=>{setTable(e.target.value);setScopeCount(null);setScopeCountCurrent(false);}}>
            {(metadata?.tables||[]).map(x=><option key={x.name} value={x.name}>{x.label} ({x.name})</option>)}
          </select></label>
          <label><span>Created from</span><input type="date" value={fromDate} onChange={e=>{setFromDate(e.target.value);setScopeCount(null);setScopeCountCurrent(false);}}/></label>
          <label><span>Created to</span><input type="date" value={toDate} onChange={e=>{setToDate(e.target.value);setScopeCount(null);setScopeCountCurrent(false);}}/></label>
          <label className="span-three"><span>Encoded query <em>optional</em></span><input value={query} onChange={e=>{setQuery(e.target.value);setScopeCount(null);setScopeCountCurrent(false);}} placeholder="e.g. active=false^priorityIN1,2"/></label>
        </div>
        {metadata?.tableDiscovery?.ok&&<div className="sn-discovery-status success">
          <b>Task hierarchy loaded</b>
          <span>{metadata.tableDiscovery.message}</span>
        </div>}
        <div className="sn-scope-count-row">
          <button className="pa-secondary" disabled={!!busy} onClick={()=>countScope()}>
            {busy==='count'?'Counting…':'Count records'}
          </button>
          {scopeCount!=null
            ? <div className={`sn-scope-count ${scopeCount>50000?'over-limit':''}`}><b>{fmt(scopeCount)}</b><span>records in scope</span></div>
            : <span className="sn-count-hint">Count the current scope before continuing.</span>}
        </div>
      </div>
      <div className="metricus-panel-footer"><button className="pa-secondary" onClick={onBack}>Back</button><button className="pa-primary" disabled={!!busy} onClick={validateScope}>{busy==='validate'?'Validating…':'Validate & continue'}</button></div>
    </section>}

    {stage===2&&<section className="panel sn-builder-panel">
      <div className="panel-head"><div><h2>Model details</h2><p>Describe the business process, not the ServiceNow configuration.</p></div><span className="count-pill">{fmt(scopeCount)} records</span></div>
      <div className="panel-body">
        <div className="sn-model-grid">
          <label><span>Model name *</span><input value={model.name} onChange={e=>setModel({...model,name:e.target.value})}/></label>
          <label><span>Process *</span><input value={model.processType} onChange={e=>setModel({...model,processType:e.target.value})}/></label>
          <label><span>Category</span><input value={model.category} onChange={e=>setModel({...model,category:e.target.value})}/></label>
          <label><span>Case / entity name</span><input value={model.entityLabel} onChange={e=>setModel({...model,entityLabel:e.target.value})}/></label>
          <label className="span-two"><span>Description</span><textarea rows={2} value={model.description} onChange={e=>setModel({...model,description:e.target.value})}/></label>
        </div>
      </div>
      <div className="metricus-panel-footer"><button className="pa-secondary" onClick={()=>setStage(1)}>Back</button><button className="pa-primary" disabled={!model.name.trim()||!model.processType.trim()} onClick={()=>setStage(3)}>Continue</button></div>
    </section>}


    {stage===3&&<OrganizationContextStep
      value={organizationContext}
      onChange={setOrganizationContext}
      repository={repository}
      sourceSystem="ServiceNow"
      isExisting={Boolean(initial?.id)}
      modelContext={{name:model.name,category:model.category,processType:model.processType,description:model.description,entityLabel:model.entityLabel}}
      onBack={()=>setStage(2)}
      onContinue={()=>setStage(4)}
    />}

    {stage===4&&<section className="panel sn-builder-panel metricus-wizard-panel">
      <div className="metricus-panel-head">
        <div>
          <h2>Activity & attributes</h2>
          <p>Choose the activity that defines the process sequence and the case characteristics used by Segments and AI.</p>
        </div>
      </div>

      <div className="metricus-panel-body metricus-attributes-body">
        <div className="metricus-activity-card">
          <MetricusSelect
            label="Activity field"
            value={activityFieldId}
            onChange={e=>setActivityFieldId(e.target.value)}
            options={fields.map(f=>({value:f.id,label:fieldName(f)}))}
          />
          <div className="metricus-activity-copy">
            <b>{fieldName(activityField)} drives the process map</b>
            <span>Process Advisor reconstructs the ordered audit history of this ServiceNow field.</span>
          </div>
        </div>

        <div className="metricus-characteristics-card metricus-characteristics-card-v2">
          <div className="metricus-characteristics-head">
            <div>
              <h3>Selected characteristics</h3>
              <p>{selectedFieldIds.length} selected</p>
            </div>
          </div>

          {selectedAttributeFields.length
            ? <div className="metricus-selected-attribute-grid">
                {selectedAttributeFields.map(f=><div className="metricus-selected-attribute-row" key={f.id}>
                  <div className="metricus-selected-attribute-name">
                    <span><b>{fieldName(f)}</b><small>{f.id}</small></span>
                    <button type="button" onClick={()=>toggleField(f.id)} title={`Remove ${fieldName(f)}`} aria-label={`Remove ${fieldName(f)}`}><X size={15}/></button>
                  </div>
                  <MetricusInput
                    value={attributeNotes[f.id]||''}
                    onChange={e=>setAttributeNotes({...attributeNotes,[f.id]:e.target.value})}
                    placeholder="Business meaning for AI (optional)"
                  />
                </div>)}
              </div>
            : <div className="metricus-selected-empty">No characteristics selected.</div>}
        </div>

        <div className="metricus-available-fields">
          <div className="metricus-available-fields-head">
            <div>
              <h3>Add characteristics</h3>
              <p>Select additional ServiceNow fields to use for Segments and AI.</p>
            </div>
            <span>{selectedFieldIds.length} selected</span>
          </div>

          <div className="metricus-attribute-toolbar">
            <div className="metricus-search">
              <Search size={16}/>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search ServiceNow fields"/>
            </div>
            <span>{fields.filter(f=>!CORE_FIELD_IDS.has(f.id)&&f.id!==activityFieldId).length} available</span>
          </div>

          <div className="metricus-available-field-grid">
            {fields
              .filter(f=>!CORE_FIELD_IDS.has(f.id)&&f.id!==activityFieldId)
              .filter(f=>{
                const q=search.trim().toLowerCase();
                return !q||`${fieldName(f)} ${f.id||''}`.toLowerCase().includes(q);
              })
              .sort((a,b)=>{
                const sa=selectedFieldIds.includes(a.id)?0:1;
                const sb=selectedFieldIds.includes(b.id)?0:1;
                return sa-sb||fieldName(a).localeCompare(fieldName(b));
              })
              .map(f=>{
                const checked=selectedFieldIds.includes(f.id);
                return <label className={`metricus-available-field ${checked?'selected':''}`} key={f.id}>
                  <input type="checkbox" checked={checked} onChange={()=>toggleField(f.id)}/>
                  <span><b>{fieldName(f)}</b><small>{f.id}</small></span>
                </label>;
              })}
          </div>
        </div>
      </div>

      <div className="metricus-panel-footer">
        <button className="pa-secondary" onClick={()=>setStage(3)}>Back</button>
        <div className="metricus-footer-actions">
          <span>{selectedFieldIds.length} characteristics selected</span>
          <button className="pa-primary" disabled={!!busy} onClick={loadPreview}>
            {busy==='preview'?'Loading preview…':'Preview data'}<ChevronRight size={15}/>
          </button>
        </div>
      </div>
    </section>}

    {stage===5&&<section className="panel sn-builder-panel metricus-wizard-panel metricus-preview-panel-v2">
      <div className="metricus-preview-toolbar-v2">
        <div>
          <h2>Preview data</h2>
          <span>{fmt(scopeCount)} records · Activity: {fieldName(activityField)} · {selectedFieldIds.length} characteristics · {conn?.name||'ServiceNow'}</span>
        </div>
        <span className="metricus-preview-note-v2">Sample rows before full extraction</span>
      </div>
      <div className="metricus-preview-grid-v2">
        <MetricusDataGrid
          rowData={preview?.records||[]}
          columnDefs={previewColumns}
          height={Math.min(610,Math.max(360,44*((preview?.records||[]).length||1)+42))}
          emptyMessage="No preview rows returned"
        />
      </div>
      <div className="metricus-panel-footer">
        <button className="pa-secondary" onClick={()=>setStage(4)}>Back</button>
        <button className="pa-primary" disabled={!!busy} onClick={()=>setStage(6)}>Continue<ChevronRight size={15}/></button>
      </div>
    </section>}

    {stage===6&&<section className="panel sn-builder-panel">
      <div className="panel-head"><div><h2>Run Process Advisor</h2><p>Confirm the processing allowance and choose whether processed data may be retained.</p></div></div>
      <div className="panel-body sn-run-body">
        <div className="metricus-processing-summary-v2">
          <div><span>Records in configured scope</span><strong>{fmt(scopeCount)}</strong></div>
          <div><span>Available this run</span><strong>{fmt(visibleProcessingLimit)}</strong></div>
          <div><span>Access</span><strong>{paidBySubscription?'Full access':'Free trial'}</strong></div>
        </div>

        {partialRun&&<div className="metricus-partial-run-v2"><div><b>{fmt(visibleProcessingLimit)} of {fmt(scopeCount)} records will be processed.</b><span>The model definition still represents the full configured scope.</span></div></div>}
        <label className={`metricus-retention-card-v2 ${retainProcessData?'selected':''}`}>
          <input type="checkbox" checked={retainProcessData} onChange={e=>setRetainProcessData(e.target.checked)}/>
          <span><b>Save processed data</b><small>Save the processed records, events and characteristics so the model can reopen without extracting them again. The model definition is always saved.</small></span>
          <em>{retainProcessData?`Will save up to ${fmt(visibleProcessingLimit)} records`:'Processed data will not be retained'}</em>
        </label>
      </div>
      <div className="metricus-panel-footer"><button className="pa-secondary" disabled={!!busy} onClick={()=>setStage(5)}>Back</button><button className="pa-primary" disabled={!!busy} onClick={run}>Extract & run</button></div>
    </section>}
    {busy==='run'&&<div className="jira-extraction-overlay" role="dialog" aria-modal="true" aria-label="ServiceNow model extraction progress">
      <div className="jira-extraction-dialog">
        <div className="jira-extraction-top">
          <div><span>PROCESS ADVISOR</span><h2>{extractProgress.percent<100?'Preparing model extraction':'Model ready'}</h2><p>{extractProgress.detail}</p></div>
          <strong>{extractProgress.percent}%</strong>
        </div>
        <div className="jira-progress-track"><div className="jira-progress-fill" style={{width:`${extractProgress.percent}%`}}/></div>
        <div className="jira-extraction-stats">
          <div><span>STATUS</span><b>{extractProgress.status}</b></div>
          <div><span>STEP</span><b>{extractProgress.step}</b></div>
        </div>
        <div className="jira-extraction-model"><Database size={18}/><div><b>{model.name}</b><span>{conn?.name||'ServiceNow'} · processing up to {fmt(visibleProcessingLimit)} of {fmt(scopeCount)} records</span></div></div>
        <div className="jira-extraction-note">Process Advisor is retrieving ServiceNow data and preparing the canonical process model. The dialog closes when the model is ready.</div>
      </div>
    </div>}
  </div>;
}
